import type { useGetReservationRequests } from "@/api/admin/useGetReservationRequests";
import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { useEffect, useState } from "react";

export interface UsePendingRequestsReturn {
  pendingRequests: ReservationRequestDto[];
  addRequest: (request: ReservationRequestDto) => void;
  removeRequest: (requestId: number) => void;
  setRequests: (requests: ReservationRequestDto[]) => void;
}

/**
 * Hook to manage pending reservation requests state
 * Handles syncing from API and add/remove operations with automatic sorting
 */
export function usePendingRequests(
  requestsResponse: ReturnType<typeof useGetReservationRequests>["data"]
): UsePendingRequestsReturn {
  const [pendingRequests, setPendingRequests] = useState<ReservationRequestDto[]>([]);

  // Sync pending requests from API
  useEffect(() => {
    if (requestsResponse?.reservationRequests) {
      setPendingRequests(requestsResponse.reservationRequests);
    }
  }, [requestsResponse]);

  // Sort requests by preferred time (earliest first)
  const sortRequests = (requests: ReservationRequestDto[]): ReservationRequestDto[] => {
    return requests.sort((a, b) => {
      const aTime = a.preferredTimes.length > 0 ? new Date(a.preferredTimes[0]) : new Date(0);
      const bTime = b.preferredTimes.length > 0 ? new Date(b.preferredTimes[0]) : new Date(0);
      return aTime.getTime() - bTime.getTime();
    });
  };

  const addRequest = (request: ReservationRequestDto) => {
    setPendingRequests((prev) => {
      const restored = [...prev, request];
      return sortRequests(restored);
    });
  };

  const removeRequest = (requestId: number) => {
    setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
  };

  const setRequests = (requests: ReservationRequestDto[]) => {
    setPendingRequests(requests);
  };

  return {
    pendingRequests,
    addRequest,
    removeRequest,
    setRequests,
  };
}
